import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://activikid.hk';

const DEFAULT_SEO = {
    title: 'ActiviKid | Kids Activities Hong Kong - AI-Powered Activity Recommendations',
    description: 'ActiviKid is a smart AI platform connecting parents with curated kids activities in Hong Kong. Get personalized extracurricular activity (課外活動) recommendations for your child.',
};

const SEO_CONFIG = {
    '/': {
        en: {
            title: 'ActiviKid | Kids Activities Hong Kong - AI-Powered Activity Recommendations',
            description: 'ActiviKid is a smart AI platform connecting parents with curated kids activities in Hong Kong. Get personalized extracurricular activity (課外活動) recommendations for your child.',
        },
        zh: {
            title: 'ActiviKid | 2026課外活動推薦 - 香港課外活動預訂APP',
            description: 'ActiviKid 係香港課外活動預訂APP，提供最新 2026課外活動推薦。AI 為子女推薦最合適嘅兒童課外活動，涵蓋興趣班、體育、STEM、藝術等。',
        },
    },
    '/faqs': {
        en: { title: 'FAQs | ActiviKid Kids Activities Platform Hong Kong', description: 'Answers to common questions about ActiviKid: how our AI activity recommendations work, enrolment, scheduling and pricing.' },
        zh: { title: '常見問題 | ActiviKid 香港課外活動推薦平台', description: 'ActiviKid 常見問題：AI 課外活動推薦點樣運作？如何報名、排期及收費？' },
    },
    '/faqs/:sectionName': {
        en: { title: 'FAQs - {name} | ActiviKid', description: 'ActiviKid frequently asked questions about {name}.' },
        zh: { title: '常見問題 - {name} | ActiviKid', description: 'ActiviKid 關於 {name} 的常見問題。' },
    },
    '/faqs/searchResult': {
        en: { title: 'FAQ Search | ActiviKid', description: 'Search ActiviKid frequently asked questions.' },
        zh: { title: '常見問題搜尋 | ActiviKid', description: '搜尋 ActiviKid 常見問題。' },
    },
    '/blogs': {
        en: { title: 'ActiviKid Blog | Kids Activities Tips & Guides Hong Kong', description: 'Practical guides and tips on choosing kids activities, extracurricular classes and after-school programmes in Hong Kong.' },
        zh: { title: 'ActiviKid 網誌 | 香港課外活動指南與推薦', description: '香港課外活動選擇指南、兒童興趣班推薦、暑假課程推薦等實用資訊。' },
    },
    '/blogs/:searchName': {
        en: { title: '{name} | ActiviKid Blog', description: 'ActiviKid blog article about {name}.' },
        zh: { title: '{name} | ActiviKid 網誌', description: 'ActiviKid 網誌文章：{name}。' },
    },
    '/about': {
        en: { title: 'About Us | ActiviKid Kids Activity Platform Hong Kong', description: 'ActiviKid is an HKSTP-backed startup building an AI platform that matches children with the extracurricular activities that fit them best.' },
        zh: { title: '關於我們 | ActiviKid 香港課外活動平台', description: 'ActiviKid 是位於香港科學園（HKSTP）的初創公司，用 AI 為小朋友配對最合適的課外活動。' },
    },
    '/partners': {
        en: { title: 'Our Partners | ActiviKid Kids Activities Hong Kong', description: 'Meet the activity providers, education centres and partners on the ActiviKid platform in Hong Kong.' },
        zh: { title: '合作夥伴 | ActiviKid 香港課外活動平台', description: '認識 ActiviKid 平台上的香港課外活動機構及合作夥伴。' },
    },
    '/partners/:partnerName': {
        en: { title: '{name} | ActiviKid Partners', description: '{name} offers kids activities on the ActiviKid Hong Kong platform.' },
        zh: { title: '{name} | ActiviKid 合作夥伴', description: '{name} 在 ActiviKid 平台提供香港兒童課外活動。' },
    },
    '/list-my-business': {
        en: { title: 'List Your Business | ActiviKid Partner - Reach HK Parents', description: 'Activity providers: list your kids classes on ActiviKid and reach parents across Hong Kong. Free partner registration.' },
        zh: { title: '加入我們 | ActiviKid 合作夥伴招募', description: '課外活動機構：加入 ActiviKid 平台，讓更多香港家長發現你的兒童課程。免費登記。' },
    },
    '/ourstory': {
        en: { title: 'Our Story | ActiviKid', description: 'The story behind ActiviKid - building an AI kids activities platform in Hong Kong.' },
        zh: { title: '我們的故事 | ActiviKid', description: 'ActiviKid 的創業故事 - 在香港打造 AI 兒童課外活動平台。' },
    },
    '/terms-and-conditions': {
        en: { title: 'Terms of Use | ActiviKid', description: 'Terms and conditions for using the ActiviKid kids activities platform.' },
        zh: { title: '使用條款 | ActiviKid', description: 'ActiviKid 香港課外活動平台的使用條款。' },
    },
    '/privacy': {
        en: { title: 'Privacy Policy | ActiviKid', description: 'How ActiviKid collects and protects your data on our kids activities platform.' },
        zh: { title: '隱私政策 | ActiviKid', description: 'ActiviKid 平台如何收集及保護您的個人資料。' },
    },
    '/plans': {
        en: { title: 'Plans & Pricing | ActiviKid Kids Activities Membership', description: 'Choose an ActiviKid membership plan to book kids activities across Hong Kong. Simple pricing for busy parents.' },
        zh: { title: '學分計劃 | ActiviKid 課外活動平台', description: '選擇 ActiviKid 學分計劃，為孩子靈活安排香港兒童課外活動。' },
    },
    '/join-us': {
        en: { title: 'Join ActiviKid | Kids Activities App Hong Kong', description: 'Join ActiviKid today and get personalized kids activities recommendations in Hong Kong.' },
        zh: { title: '加入 ActiviKid | 香港兒童課外活動平台', description: '立即加入 ActiviKid，獲取專屬的香港課外活動推薦。' },
    },
    '/partner-login': {
        en: { title: 'Partner Login | ActiviKid', description: 'Log in to the ActiviKid partner portal to manage your kids activities listings.' },
        zh: { title: '合作夥伴登入 | ActiviKid', description: '登入 ActiviKid 合作夥伴後台，管理您的課外活動課程。' },
    },
    '/partner-register': {
        en: { title: 'Partner Registration | ActiviKid - List Kids Activities', description: 'Register as an ActiviKid partner and list your kids classes and activities in Hong Kong.' },
        zh: { title: '合作夥伴註冊 | ActiviKid - 刊登課程', description: '成為 ActiviKid 合作夥伴，在平台刊登您的兒童興趣班及活動。' },
    },
    '/discover-more': {
        en: { title: 'Discover More | Kids Activity Recommendations Hong Kong', description: 'Discover how ActiviKid uses AI to recommend the best kids activities in Hong Kong.' },
        zh: { title: '了解更多 | 課外活動推薦 - ActiviKid', description: '了解更多 ActiviKid 如何用 AI 推薦香港兒童課外活動。' },
    },
    '/download': {
        en: { title: 'Download ActiviKid App | Kids Activities Hong Kong', description: 'Download the ActiviKid app to discover and book the best kids activities and extracurricular classes in Hong Kong.' },
        zh: { title: '下載 ActiviKid APP | 香港課外活動預訂', description: '下載 ActiviKid APP，用 AI 幫你搵到最適合小朋友嘅課外活動，香港課外活動預訂平台。' },
    },
    '/contact': {
        en: { title: 'Contact Us | ActiviKid Kids Activities Hong Kong', description: 'Get in touch with ActiviKid - general, media, partnership and business enquiries for our Hong Kong kids activities platform.' },
        zh: { title: '聯絡我們 | ActiviKid 香港課外活動平台', description: '聯絡 ActiviKid - 一般查詢、媒體查詢、合作及商務查詢，香港兒童課外活動平台。' },
    },
};

function matchSeoConfig(pathname) {
    const lang = pathname.startsWith('/hk') ? 'zh' : 'en';
    const p = lang === 'zh' ? pathname.replace(/^\/hk/, '') || '/' : pathname;

    let best = null;
    for (const [pattern, seo] of Object.entries(SEO_CONFIG)) {
        if (pattern.includes(':')) {
            const pat = pattern.replace(/\/:[^/]+/g, '/[^/]+');
            if (new RegExp(`^${pat}$`).test(p)) {
                if (!best || pattern.length > best.pattern.length) {
                    const segs = pattern.split('/').filter(Boolean);
                    const parts = p.split('/').filter(Boolean);
                    const name = segs
                        .map((s, i) => (s.startsWith(':') ? decodeURIComponent(parts[i] || '') : null))
                        .filter(Boolean)
                        .join(' ')
                        .split('-')
                        .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
                        .join(' ');
                    best = { pattern, seo: seo[lang], name };
                }
            }
        } else if (p === pattern) {
            best = { pattern, seo: seo[lang], name: '' };
        }
    }
    return best ? { ...best.seo, name: best.name } : null;
}

function setMeta(attr, key, content) {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
}

function setLink(rel, href, hreflang) {
    let el = document.head.querySelector(`link[rel="${rel}"][data-seo="1"][hreflang="${hreflang || ''}"]`);
    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        el.setAttribute('data-seo', '1');
        if (hreflang) el.setAttribute('hreflang', hreflang);
        document.head.appendChild(el);
    }
    el.setAttribute('href', href);
}

function fillPlaceholders(text, name) {
    return name ? text.replace(/\{name\}/g, name) : text.replace(/\s*\{name\}/g, '').trim();
}

function Seo() {
    const location = useLocation();

    useEffect(() => {
        const seo = matchSeoConfig(location.pathname) || DEFAULT_SEO;
        const name = seo.name || '';
        const title = fillPlaceholders(seo.title, name);
        const description = fillPlaceholders(seo.description, name);
        const isZh = location.pathname.startsWith('/hk');
        const canonicalPath = isZh ? `/hk${location.pathname.replace(/^\/hk/, '')}` : location.pathname;
        const canonical = `${SITE_URL}${canonicalPath === '/' ? '/' : canonicalPath}`;

        document.title = title;
        setMeta('name', 'description', description);
        setMeta('property', 'og:title', title);
        setMeta('property', 'og:description', description);
        setMeta('property', 'og:url', canonical);
        setMeta('property', 'twitter:title', title);
        setMeta('property', 'twitter:description', description);
        setLink('canonical', canonical);
        setLink('alternate', `${SITE_URL}${location.pathname.replace(/^\/hk/, '') || '/'}`, 'en-hk');
        setLink('alternate', `${SITE_URL}/hk${location.pathname.replace(/^\/hk/, '') || '/'}`, 'zh-hk');
        document.documentElement.lang = isZh ? 'zh-HK' : 'en';
    }, [location.pathname]);

    return null;
}

export default Seo;
